import React from 'react';
import { AlertTriangle, CheckCircle2, PlayCircle, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  useApproveInterpreterSuggestion,
  useInterpreterSuggestions,
  useRejectInterpreterSuggestion,
  useReplayInterpreterPayload,
} from './api';

export default function InterpreterDlqTriageTab() {
  const suggestionsQ = useInterpreterSuggestions('pending');
  const approveMutation = useApproveInterpreterSuggestion();
  const rejectMutation = useRejectInterpreterSuggestion();
  const replayMutation = useReplayInterpreterPayload();

  const [rejectionReason, setRejectionReason] = React.useState('');
  const [payloadText, setPayloadText] = React.useState('{\n  "device_id": "demo-device",\n  "tenant_id": "dev",\n  "telemetry": {\n    "temperature": 21.5\n  }\n}');
  const [topic, setTopic] = React.useState('');
  const [message, setMessage] = React.useState<string | null>(null);

  async function handleReplay() {
    try {
      setMessage(null);
      const payload = JSON.parse(payloadText);
      await replayMutation.mutateAsync({ payload, topic: topic || undefined });
      setMessage('Replay submitted to Pulsar.');
    } catch (error: any) {
      setMessage(error?.message || 'Replay failed');
    }
  }

  return (
    <div className="space-y-6 pt-4 animate-fade-in">
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            <h2 className="text-sm font-semibold text-gray-900">Pending Suggestions</h2>
          </div>
          {suggestionsQ.isLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 3 }).map((_, idx) => (
                <div key={idx} className="h-24 rounded-lg bg-gray-50 animate-pulse" />
              ))}
            </div>
          ) : (suggestionsQ.data || []).length === 0 ? (
            <div className="rounded-lg border border-dashed border-gray-200 p-8 text-center text-sm text-gray-500">
              No pending suggestions. The interpreter queue is clean.
            </div>
          ) : (
            <div className="space-y-3">
              {(suggestionsQ.data || []).map((suggestion) => (
                <div key={suggestion._id} className="rounded-xl border border-gray-200 p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold text-gray-900">{suggestion.type || 'suggestion'}</div>
                      <div className="text-xs text-gray-500 mt-1">
                        Confidence {Math.round((suggestion.confidence || 0) * 100)}%
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        onClick={() => approveMutation.mutate(suggestion._id)}
                        disabled={approveMutation.isPending}
                      >
                        <CheckCircle2 className="w-4 h-4 mr-1" />
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => rejectMutation.mutate({ suggestionId: suggestion._id, reason: rejectionReason })}
                        disabled={rejectMutation.isPending}
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        Reject
                      </Button>
                    </div>
                  </div>
                  {suggestion.reasoning ? (
                    <p className="text-xs text-gray-600 mt-3">{suggestion.reasoning}</p>
                  ) : null}
                  <pre className="mt-3 rounded-lg bg-gray-50 p-3 text-[10px] text-gray-600 whitespace-pre-wrap break-all">
                    {JSON.stringify(suggestion.suggestedData || {}, null, 2)}
                  </pre>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="text-sm font-semibold text-gray-900 mb-3">Reject reason</div>
            <Textarea
              value={rejectionReason}
              onChange={(event) => setRejectionReason(event.target.value)}
              rows={5}
              placeholder="Optional context for rejection."
            />
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-3">
              <PlayCircle className="w-4 h-4 text-indigo-600" />
              <div className="text-sm font-semibold text-gray-900">Replay Payload</div>
            </div>
            <input
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm mb-3"
              value={topic}
              onChange={(event) => setTopic(event.target.value)}
              placeholder="Optional Pulsar topic override"
            />
            <Textarea value={payloadText} onChange={(event) => setPayloadText(event.target.value)} rows={10} />
            {message ? <div className="text-xs text-gray-600 mt-3">{message}</div> : null}
            <Button onClick={handleReplay} disabled={replayMutation.isPending} className="w-full mt-3">
              <PlayCircle className="w-4 h-4 mr-2" />
              {replayMutation.isPending ? 'Submitting...' : 'Replay Event'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
